import React, { memo } from 'react'
import DetailCaption from 'components/TrackingContextCard/DetailCaption'
import { DetailItem } from 'components/TrackingContextCard/DetailItem'
import { FormattedDateTimeRange } from 'transfix-ui/components/FormattedDateTimeRange'
import { Box, makeStyles } from 'transfix-ui/core'
import { APPOINTMENT_TYPES } from 'types'
import { INextStop } from 'types/Location'
import { IShipmentStopAppointmentTypes } from 'types/graphqlTypes'
import { FormattedDateTime, MapMarker, Tag, TAG_SIZE } from 'transfix-ui'
import { DISPLAY } from 'constants/display'
import PopupTitle from './PopupTitle'

const useStopComponentStyles = makeStyles({
  wrapContent: {
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
})

interface IStopCardProps {
  properties: GeoJSON.GeoJsonProperties | null
}

const AppointmentTypeMapper: Record<IShipmentStopAppointmentTypes, string> = {
  appointment: 'Appointment',
  range: 'Range',
}

const parseStop = (stopData: string | undefined): INextStop => {
  return JSON.parse(stopData || '')
}

export const StopCard = memo<IStopCardProps>(({ properties }) => {
  const { data: stopData, sequence = 1 } = properties || {}
  const { appointment, facility, status, type } = parseStop(stopData) || {}
  const { appointmentType: appointmentTypeProp, endAt, startAt } = appointment || {}
  const { address, addressLine2, city, companyName, stateCode, zipcode } = facility || {}
  const { timezone = 'America/New_York' } = status || {}
  const title = type === 'pickup' ? `Pickup ${sequence}` : `Delivery ${sequence}`
  const appointmentType =
    appointmentTypeProp === APPOINTMENT_TYPES.APPT
      ? IShipmentStopAppointmentTypes.appointment
      : IShipmentStopAppointmentTypes.range

  const classes = useStopComponentStyles()
  return (
    <Box width={187}>
      <Box display='flex'>
        <Box mr={2}>
          <MapMarker sequence={sequence} type={type} />
        </Box>
        <PopupTitle>{title}</PopupTitle>
      </Box>
      <Box mt={3}>
        <DetailItem
          titleWidth={60}
          descriptionWidth={136}
          title='Facility'
          value={<div className={classes.wrapContent}>{companyName}</div>}
          isLoading={false}
        />
        <DetailItem
          titleWidth={60}
          descriptionWidth={136}
          title='Address'
          value={
            <div>
              <DetailCaption>{address || ''}</DetailCaption>
              {addressLine2 && <DetailCaption>{addressLine2}</DetailCaption>}
              <DetailCaption>
                {city}, {stateCode} {zipcode}
              </DetailCaption>
            </div>
          }
          isLoading={false}
        />
        <DetailItem
          titleWidth={60}
          title='Type'
          value={appointmentType ? AppointmentTypeMapper[appointmentType] : DISPLAY.empty}
          isLoading={false}
        />
        <DetailItem
          titleWidth={60}
          descriptionWidth={136}
          title='Date'
          value={
            <FormattedDateTime
              datetime={startAt || ''}
              displayTimezone={timezone}
              format='weekdayDateAndYear'
            />
          }
          isLoading={false}
        />
        <DetailItem
          titleWidth={60}
          descriptionWidth={136}
          title='Appt'
          value={
            <Tag
              size={TAG_SIZE.xsmall}
              label={
                <FormattedDateTimeRange
                  startAt={startAt || ''}
                  endAt={endAt || ''}
                  startAtTimezone={timezone}
                  endAtTimezone={timezone}
                  format='onlyTime'
                  showTimezone
                />
              }
            />
          }
          isLoading={false}
        />
      </Box>
    </Box>
  )
})
